import { LitElement, css, html, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";

export type ChatSuggestion = {
  id?: string | number;
  label: string;
  question: string;
};

/**
 * The one presentation for "a question Jonas offers you": the chat-home prompts before the
 * first question and the follow-ups after an answer. Both are a list of questions the visitor
 * can send with one click, so both render here and emit the same jonas-question-submit event.
 */
@customElement("help-jonas-suggestions")
export class HelpJonasSuggestions extends LitElement {
  static override styles = css`
    :host {
      display: block;
      min-width: 0;
    }

    /*
     * Each row is only as wide as its own question, all starting at the same left edge,
     * so the list reads as a column of offers rather than a block of buttons.
     */
    .suggestions {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      min-width: 0;
      gap: 2px;
    }

    .suggestions afas-button {
      max-width: 100%;
      --font-weight: 400;
      /* Reads as an offer, not as a link: the primary blue would pull the eye away
         from the composer these sit under. */
      --foreground-color: var(--color-text-on-background-700);
      --icon-color: var(--color-text-on-background-700);
    }

    /*
     * Follows afas-menu-item, which afas-chat-choices builds its options on: one line,
     * cut off with an ellipsis. afas-button sizes its inner button to its text and lets it
     * run straight out of the host, so both parts need reining in -- and min-width zero,
     * or the flex item refuses to shrink below its text and never truncates. The tooltip
     * carries the whole question.
     */
    .suggestions afas-button::part(button) {
      max-width: 100%;
    }

    .suggestions afas-button::part(label) {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .suggestions--enter afas-button {
      animation: suggestion-appear 240ms ease-out both;
    }

    .suggestions--enter afas-button:nth-child(2) {
      animation-delay: 60ms;
    }

    .suggestions--enter afas-button:nth-child(3) {
      animation-delay: 120ms;
    }

    /*
     * Leaving: the question the visitor picked travels toward the conversation it is
     * about to open, so the list slides out to the right as the composer drops to the
     * bottom instead of blinking out from under it.
     */
    .suggestions--exit {
      pointer-events: none;
    }

    .suggestions--exit afas-button {
      animation: suggestion-exit 240ms ease-in both;
    }

    .suggestions--exit afas-button:nth-child(2) {
      animation-delay: 40ms;
    }

    .suggestions--exit afas-button:nth-child(3) {
      animation-delay: 80ms;
    }

    @keyframes suggestion-appear {
      from {
        opacity: 0;
        transform: translateY(8px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes suggestion-exit {
      from {
        opacity: 1;
        transform: translateX(0);
      }
      to {
        opacity: 0;
        transform: translateX(24px);
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .suggestions--enter afas-button,
      .suggestions--exit afas-button {
        animation: none;
      }

      .suggestions--exit {
        opacity: 0;
      }
    }

    @media print {
      :host {
        display: none;
      }
    }
  `;

  @property({ attribute: false })
  accessor suggestions: ChatSuggestion[] = [];

  @property()
  accessor listLabel = "Suggesties";

  @property({ type: Boolean })
  accessor disabled = false;

  // Set for the one render in which the list replaces a loading placeholder, so the
  // questions fade in once instead of on every later re-render.
  @property({ type: Boolean })
  accessor entrance = false;

  // Set while the list is on its way out, so it can finish its animation before the
  // parent drops it from the DOM.
  @property({ type: Boolean })
  accessor exiting = false;

  protected override render() {
    if (this.suggestions.length === 0) return nothing;

    return html`<div
      class=${this.listClasses()}
      role="group"
      aria-label=${this.listLabel}
      aria-hidden=${this.exiting ? "true" : nothing}
    >
      ${this.suggestions.map(
        (suggestion) =>
          html`<afas-button
            variant="minimal"
            data-suggestion-id=${suggestion.id ?? nothing}
            label=${suggestion.label}
            tooltip=${suggestion.question}
            ?disabled=${this.disabled || this.exiting}
            @click=${() => this.submit(suggestion)}
          >
            <afas-icon
              slot="icon"
              icon="ArrowForwardOutlined"
              size="small"
            ></afas-icon>
          </afas-button>`,
      )}
    </div>`;
  }

  private listClasses(): string {
    if (this.exiting) return "suggestions suggestions--exit";
    return this.entrance ? "suggestions suggestions--enter" : "suggestions";
  }

  private submit(suggestion: ChatSuggestion): void {
    this.dispatchEvent(
      new CustomEvent("jonas-question-submit", {
        detail: { message: suggestion.question, suggestionId: suggestion.id },
        bubbles: true,
        composed: true,
      }),
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "help-jonas-suggestions": HelpJonasSuggestions;
  }
}
